import vuex from '@/store'
import api from '@/api'
import tinycolor from 'tinycolor2'
import * as levenshtein from 'fastest-levenshtein'
import { systemTemplates } from './stagehandTemplates'
import { stagehandUtils } from './stagehandUtils'
import _ from 'lodash'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone.js'

dayjs.extend(utc)
dayjs.extend(relativeTime)
dayjs.extend(timezone)

// Characters not allowed in upload filenames
const INVALID_FILENAME_CHARS = /[~''\u201C\u201D#%&*:<>?/\\{|｜}+]/

function hasInvalidFileNameChars(filename) {
  return INVALID_FILENAME_CHARS.test(filename?.toString() ?? '')
}

// Legacy alias — returns true when filename HAS invalid chars (name is misleading but callers depend on it)
const isValidFileName = hasInvalidFileNameChars

function sanitizeFilename(filename) {
  if (!filename) return filename
  return filename
    .replace(new RegExp(INVALID_FILENAME_CHARS.source, 'g'), '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}
function isImage(url) {
  let extensions = ['gif', 'png', 'bmp', 'jpg', 'jpeg', 'svg']
  let urlExtension = url.split('.').pop().toLowerCase()
  return extensions.includes(urlExtension)
}

/**
 * Download a file from a given URL
 * @param {string} url - The URL of the file to download
 * @param {string} filename - Optional custom filename for the download
 */
function downloadFile(url, filename = null) {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.download = filename || url.split('/').pop() || 'download'
  document.body.appendChild(link)
  link.click()
  link.remove()
}
/**
 * Check if story is in the past
 * @param {Object} story
 * @returns Boolean
 */
function isStoryArchived(story) {
  let tz = vuex.getters.currentTimezone
  const today = dayjs.tz(Date.now(), tz).format('YYYY-MM-DD')

  if (story.story_date < today) {
    return true
  }

  return false
}

/**
 * Taks a number of seconds and convert to string with format 'mm:ss'
 * @param {Number} time - Number representation of time in seconds
 * @returns String - String representation of time in format "mm:ss"
 */
function minutesFormat(timeStr) {
  if (timeStr == undefined) return
  let time = parseFloat(timeStr)
  if (isNaN(time)) return `Invalid Time (${timeStr})`
  const isNegative = time < 0
  time = Math.abs(time)
  let hours = Math.floor(time / 3600)
  let minutes = Math.floor(time / 60) - hours * 60
  let seconds = Math.round(time - minutes * 60)
  function str_pad_left(string, pad, length) {
    return (new Array(length + 1).join(pad) + string).slice(-length)
  }

  return (isNegative ? '-' : '') + (hours ? str_pad_left(hours, '0', 2) + ':' : '') + str_pad_left(minutes, '0', 2) + ':' + str_pad_left(seconds, '0', 2)
}

/**
 *
 * @param {String} str - Time String in format "mm:ss"
 * @returns number of seconds in time string
 */
function parseMinutes(str) {
  let elements = str?.toString().split(':')
  // parse hh:mm:ss
  if (elements.length == 3) return parseInt(elements[0]) * 60 * 60 + parseInt(elements[1]) * 60 + parseInt(elements[2])
  // parse mm:ss
  else if (elements.length == 2) return parseInt(elements[0]) * 60 + parseInt(elements[1])
  // parse ss
  else return parseInt(str)
}

function today(tz, format = 'YYYY-MM-DD') {
  if (!tz) tz = vuex.getters.currentTimezone
  if (!format) return dayjs().tz(tz)
  return dayjs().tz(tz).format(format)
}

/**
 * Check conditions for whether a story can be editted by current user
 * @param {Object} story
 * @returns Boolean
 */
function isStoryEditable(story) {
  let threeDaysAgo = dayjs().subtract(3, 'day').format('YYYY-MM-DD')
  let hourOfTheDay = dayjs().hour()

  // is this a foreign station
  if (story?.station && story?.station != vuex.getters.currentStation.station) {
    return false
  }

  // is it in the trash bin?
  if (story?.id && story.deleted_at) {
    return false
  }

  // Does the user have permissions?
  if (vuex.getters.isAdminUser || vuex.getters.isStagehandUser) {
    return true
  }

  // Redundant to above
  if (vuex.getters.isNewsDeskUser === false) {
    return false
  }

  // case for new story
  if (!story?.id) {
    return true
  }

  // Redundant to above
  if (vuex.getters.isStagehandUser) {
    return true
  }

  // check if story is TBD
  if (!story.story_date || story.story_date == '0000-00-00') {
    return true
  }

  // is the story already deep in archive
  if (!story.end_date && story.story_date > threeDaysAgo) {
    return true
  }

  // is the story already deep in archive
  if (!story.end_date && story.story_date == threeDaysAgo && hourOfTheDay <= 8) {
    return true
  }

  // is the story already deep in archive - date range
  if (story.end_date && story.end_date > threeDaysAgo) {
    return true
  }

  // is the story already deep in archive - date range
  if (story.end_date && story.end_date == threeDaysAgo && hourOfTheDay <= 8) {
    return true
  }

  return false
}

/**
 * @param {Object} media - Media object
 * @returns {Number|null} - number of milliseconds until expiration, or null if never expires
 * @description
 * Returns number of milliseconds until media expires. If media has no story attached,
 * expiration is 30 days after media was created. If media has a story attached, expiration
 * is 30 days after story date. If story date is TBD (0000-00-00), media never expires and
 * function returns null.
 */
function getMediaExpiration(media) {
  // if media archive is true return
  if (media?.archive) return null

  let expires = null

  // if there is no story attached, use created at date
  if (!media?.story) {
    expires = dayjs.tz(media.created_at, vuex.getters.currentTimezone).add(30, 'day')
  } else {
    // expires 30 days after story date
    let storyDateStr = media?.story?.story_date

    // if the story has no story date, return null because it never expires (TBD)
    if (!storyDateStr || storyDateStr == '0000-00-00') return null

    let storyDate = dayjs.tz(storyDateStr, vuex.getters.currentTimezone)
    expires = storyDate.add(30, 'day')
  }
  let now = dayjs()
  return expires.valueOf() - now.valueOf()
}

function isMediaAssetEditable(media) {
  if (media?.story) return isStoryEditable(media?.story)
  if (media?.station !== vuex.getters.currentStation.station) return false
  return vuex.getters.isNewsDeskUser === true
}

/**
 * Check conditions for whether a story can be editted by current user
 * @param {Object} story
 * @returns Boolean
 */
function isStoryDownloadable(story) {
  // is this a foreign station
  if (story?.station && story?.station != vuex.getters.currentStation.station) {
    return false
  }

  // is it in the trash bin?
  if (story?.id && story.deleted_at) {
    return false
  }

  // Does the user have permissions?
  if (vuex.getters.isAdminUser || vuex.getters.isStagehandUser) {
    return true
  }

  // Redundant to above
  if (vuex.getters.isNewsDeskUser === false) {
    return false
  }

  // case for new story
  if (!story?.id) {
    return true
  }

  // Redundant to above
  if (vuex.getters.isStagehandUser) {
    return true
  }

  // check if story is TBD
  if (!story.story_date || story.story_date == '0000-00-00') {
    return true
  }

  // is the story set
  if (!!story.story_date) {
    return true
  }

  return false
}

function isMediaAssetDownloadable(media) {
  return vuex.getters.isNewsDeskViewer === true
  if (media?.story) return isStoryDownloadable(media?.story)
  if (media?.station !== vuex.getters.currentStation.station) return false
  return vuex.getters.isNewsDeskUser === true
}

/**
 * Get stories that titled simlilar to given title
 * @param {Array<Object>} stories
 * @param {String} title
 * @returns Array<Object>
 */
function findSimilar(stories, title, distanceMultiplyer = 0.5) {
  let ret = []
  if (!title) return []
  for (const story of stories) {
    if (!story?.title) continue
    const levScore = levenshtein.distance(story.title, title)
    const threshold = title.length * distanceMultiplyer
    if (levScore < threshold) ret.push(story)
  }
  return ret
}

// convert a string date into a formatted string date
// date string should be in UTC
// if dateString is format yyyy-mm-dd, it will be not be converted to UTC
function dateFormat(dateString, format = 'LL') {
  if (!dateString) return ''
  if (dateString.length == 10) {
    return dayjs(dateString).format(format)
  }
  return dayjs.utc(dateString).tz(vuex.getters.currentTimezone).format(format)
}

// convert a string date to a js Date
function parseDate(date) {
  // Postgres likes to store timestamps with +00 (not +00:00)
  if (date.endsWith('+00')) {
    date = date + ':00'
  }

  return dayjs(date).tz(vuex.getters.currentTimezone).toDate()
}

// get date string in station tz
function getDateForView(view) {
  switch (view) {
    case 'today':
      return dayjs(new Date()).tz(vuex.getters.currentTimezone).format('YYYY-MM-DD')
    case 'tomorrow':
      return dayjs(new Date()).tz(vuex.getters.currentTimezone).add(1, 'days').format('YYYY-MM-DD')
    default:
      return
  }
}

function isDark(color) {
  return tinycolor(color).isDark()
}

/** count the number of words in a string */
function countWords(str) {
  if (!str) return 0
  return str.toString().trim().split(/\s+/).length
}

/**
 * get inner text from an XML formatted string
 * @param {String} xml
 * @returns String
 */
function getText(xml) {
  // contenteditable will add unclosed <br> tags, remove them
  const parser = new DOMParser()
  const text = xml?.replace(/<br(\s|\/)*>/g, '').replace(/&nbsp;/g, ' ') ?? ''
  let doc
  doc = parser.parseFromString(`<body>${text}</body>`, 'text/html')
  const errorNode = doc.querySelector('parsererror')
  if (errorNode) {
    return xml
  }
  return innerText(doc.querySelector('body'))
}

function transformEnpsTags(tagsObj) {
  return Object.keys(tagsObj).map((tagId) => {
    let value = parseEnpsTag(tagsObj[tagId])
    return { id: tagId, name: value, raw: tagsObj[tagId] }
  })
}

// parse the ENPS tags. tags are formatted as XML
function parseEnpsTag(strTag) {
  let matches = strTag.match(/<mosAbstract>(.*)<\/mosAbstract>/)
  if (!matches) return strTag
  return matches[1]

  const parser = new DOMParser()
  let text = strTag
    ?.replace(/^\[/, '')
    .replace(/]$/, '')
    .replace(/<(\w+)\s([^>]*?)\/>/g, '<$1 $2></$1>')
    .replace(/\s+>/g, '>')
    .replace(/\\/g, '_')
    .replace(/<mosObj>.*<\/mosObj>/g, '')

  const pattern = /[^\x09\x0A\x0D\x20-\uD7FF\uE000-\uFFFD\uD800-\uDFFF]/g

  // Use the replace() method to replace invalid characters with an empty string
  const cleanedXMLString = text.replace(pattern, '')

  let doc
  doc = parser.parseFromString(cleanedXMLString, 'application/xml')
  const errorNode = doc.querySelector('parsererror')
  if (errorNode) {
    console.warn('enps parse error', strTag, text)
    return text
  } else {
    return doc.documentElement.textContent
  }
}

function removeTagsWithClass(xml, className) {
  if (!className) return xml

  const parser = new DOMParser()
  const text =
    xml
      ?.replace(/<br(\s|\/)*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/\u200B/g, '') ?? ''
  const wrapped = `<body>${text}</body>`
  let doc
  try {
    doc = parser.parseFromString(wrapped, 'text/html')
  } catch (error) {
    return xml
  }
  const errorNode = doc.querySelector('parsererror')
  if (errorNode) {
    return xml
  } else {
    let elements = doc.getElementsByClassName(className)
    while (elements.length > 0) {
      elements[0].parentNode.removeChild(elements[0])
    }
    return doc.querySelector('body')?.innerHTML
  }
}

// HTML ONLY
function removeEmptyTags(html, tagName) {
  if (!tagName) return html

  const parser = new DOMParser()
  const text =
    html
      ?.replace(/<br(\s|\/)*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/\u200B/g, '') ?? ''
  let doc
  doc = parser.parseFromString(`<body>${text}</body>`, 'text/html')
  const errorNode = doc.querySelector('parsererror')
  if (errorNode) {
    return html
  } else {
    let elements = doc.getElementsByTagName(tagName)
    for (let i = 0; i < elements.length; i++) {
      var child = elements[i]
      if (child.tagName == tagName && child.innerHTML.trim() == '') {
        child.parentNode.removeChild(child)
      }
    }
  }
  return doc.querySelector('body').innerHTML
}

/**
 * get inner text from an xml element for recursive calling in 'GetText()'
 * @param {String} element - XML element
 * @returns String
 */
function innerText(element) {
  function getTextLoop(element) {
    const texts = []
    Array.from(element?.childNodes ?? []).forEach((node) => {
      if (node.nodeType === 3) {
        texts.push(node.textContent.trim())
      } else {
        texts.push(...getTextLoop(node))
      }
    })
    return texts
  }
  return getTextLoop(element).join(' ')
}

// https://stackoverflow.com/questions/26030209/html-and-js-surrounding-every-word-in-the-document-with-a-span-tag
function wrapText(html, idPrefix) {
  const parser = new DOMParser()
  let doc
  try {
    doc = parser.parseFromString(`<body>${html}</body>`, 'text/html')
  } catch (error) {
    return html
  }
  const errorNode = doc.querySelector('parsererror')
  if (errorNode) {
    return html
  } else {
    // First a simple implementation of recursive descent,
    // visit all nodes in the DOM and process it with a callback:
    function walkDOM(node, callback) {
      if (node.nodeName != 'SCRIPT') {
        // ignore javascript
        callback(node)
        for (var i = 0; i < node.childNodes.length; i++) {
          walkDOM(node.childNodes[i], callback)
        }
      }
    }
    // Now we can walk the DOM and find text nodes:
    var textNodes = []
    walkDOM(doc, function (n) {
      if (n.nodeType == 3) {
        textNodes.push(n)
      }
    })

    // simple utility functions to avoid a lot of typing:
    function insertBefore(new_element, element) {
      element.parentNode.insertBefore(new_element, element)
    }
    function removeElement(element) {
      element.parentNode.removeChild(element)
    }
    function makeSpan(txt, attrs) {
      var s = document.createElement('span')
      for (var i in attrs) {
        if (attrs.hasOwnProperty(i)) s[i] = attrs[i]
      }
      s.appendChild(makeText(txt))
      return s
    }
    function makeText(txt) {
      return document.createTextNode(txt)
    }

    var id_count = 1
    for (var i = 0; i < textNodes.length; i++) {
      var n = textNodes[i]
      var txt = n.nodeValue
      var words = txt.split(' ')

      // Insert span surrounded words:
      insertBefore(makeSpan(words[0], { id: `${idPrefix}${id_count++}` }), n)
      for (var j = 1; j < words.length; j++) {
        insertBefore(makeText(' '), n) // join the words with spaces
        insertBefore(makeSpan(words[j], { id: `${idPrefix}${id_count++}` }), n)
      }
      // Now remove the original text node:
      removeElement(n)
    }
    return doc.querySelector('body').innerHTML
  }
}

function detectWordSequence(html, wordList) {
  let parser = new DOMParser()
  let doc
  try {
    doc = parser.parseFromString(html, 'text/html')
  } catch (error) {
    return html
  }
  const errorNode = doc.querySelector('parsererror')
  if (errorNode) {
    return html
  } else {
    let wordListLower = wordList.map((word) => word.replace(/[^a-zA-Z0-9]/g, '').toLowerCase())
    let matches = []

    let spans = Array.from(doc.getElementsByTagName('span')).filter((span) => !span.classList.contains('pi') && !span.classList.contains('cc'))
    for (let i = 0; i < spans.length; i++) {
      // const loadedEl = document.getElementById(spans[i].id)
      // if (loadedEl && (loadedEl.classList.contains('matched-read') || loadedEl.classList.contains('past-read'))) {
      //   continue
      // }
      for (let j = 0; j < wordListLower.length; j++) {
        if (spans[i].textContent.toLowerCase().replace(/[^a-zA-Z0-9]/g, '') === wordListLower[j]) {
          matches.push(spans[i])
          // wordListLower.splice(j, 1)
        }
      }
    }
    return matches.map((m) => m.id)
  }
}

/* This function first maps the input array to an array of numbers by parsing out the numbers from the strings.
  It then sorts the numbers in ascending order.The result array will store the sequential arrays of numbers.
  The startIndex variable keeps track of the starting index of the current sequential array of numbers.
  The function iterates through the sorted array of numbers and checks if the difference between consecutive
  numbers is not equal to 1. If it is not equal to 1, then the current sequential array is added to the result
  array, and the startIndex is updated to the current index.Finally, the last sequential array is added to the
  result array, and the result is returned.
 */
function parseSequentialNumbers(arr, maxGap = 3) {
  let parsedNumbers = arr.map((string) => {
    const elements = string.split('_')
    return parseInt(elements[elements.length - 1])
  })
  let numbersWithStrings = arr.map((string, index) => ({
    string,
    number: parsedNumbers[index]
  }))
  let sortedNumbersWithStrings = numbersWithStrings.sort((a, b) => a.number - b.number)
  let currentSequence = []
  let longestSequence = []
  for (let i = 1; i < sortedNumbersWithStrings.length; i++) {
    currentSequence.push(sortedNumbersWithStrings[i - 1])
    // if the distance between the matches is within our maxGap, continue collecting
    if (sortedNumbersWithStrings[i].number - sortedNumbersWithStrings[i - 1].number <= maxGap) {
      // if this is the last number in the array
      if (i === sortedNumbersWithStrings.length - 1) {
        // put it in the current squence
        currentSequence.push(sortedNumbersWithStrings[i])
        // if this is the longest squence, set it
        if (currentSequence.length > longestSequence.length) {
          longestSequence = currentSequence
        }
      }
    } else {
      if (currentSequence.length > longestSequence.length) {
        longestSequence = currentSequence
      }
      // were outside our gap range, reset
      currentSequence = []
    }
  }
  return longestSequence.map((obj) => obj.string)
}

/**
 * Capitalize the first letter of each word
 * @param {String} str
 * @returns String
 */
function toTitleCase(str) {
  return str?.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

/**
 * Escape special characters
 * @param {String} str
 * @returns String
 */
function escape(str) {
  return str
    ?.replace(/[\\]/g, '\\\\')
    .replace(/[\"]/g, '\\"')
    .replace(/[\/]/g, '\\/')
    .replace(/[\b]/g, '\\b')
    .replace(/[\f]/g, '\\f')
    .replace(/[\n]/g, '\\n')
    .replace(/[\r]/g, '\\r')
    .replace(/[\t]/g, '\\t')
}

function groupStoriesBy(groupBy, stories) {
  // get all the dates with stories
  let groups = _.uniq(stories.map((s) => s[groupBy])).sort((a, b) => {
    if (!a) return 1
    if (!b) return -1
    return new Date(a) - new Date(b)
  })
  let groupedData = []

  // Create and append the options
  for (const group of groups) {
    let groupedStories = stories.filter((s) => s[groupBy] == group)
    groupedData.push({ group, stories: groupedStories })
  }
  return groupedData
}

function scriptListMergeInto(list, newList) {
  if (!newList || newList.length == 0) return
  // delete items that are missing from newList
  list.forEach((i) => {
    if (!newList.find((nI) => nI.id == i.id)) list.splice(list.indexOf(i), 1)
  })

  for (let i = 0; i < newList.length; i++) {
    let newItem = newList[i]
    let oldItem = list[i]

    // if they are the same id but dates have changed, update it
    if (oldItem && newItem.id == oldItem.id) {
      if (oldItem.attributes?.lastModified != newItem.attributes?.lastModified) {
        oldItem.attributes = { ...newItem.attributes }
      }
    } else {
      // Check if this script is somewhere else in the list and remove it before inserting into the new position
      let positionInOldList = list.findIndex((oI) => oI.id == newItem.id)
      if (positionInOldList >= 0) list.splice(positionInOldList, 1)
      list.splice(i, 0, newItem)
    }
  }
}

function generatePlaceholderHtml(platform) {
  const timestamp = dayjs(new Date()).tz(vuex.getters.currentTimezone).format('LLL')

  return `<div class='paragraph'>&nbsp;</div><hr><span class='pi'>${timestamp}: Pushed from Newsdesk to '${platform}'</span>`
}

function getEmployeeName(eid) {
  let employee = vuex.getters.employees.find((e) => e.empid == eid)
  return employee?.label ?? ''
}

function getUniqShares(story) {
  if (!story?.share_requests?.length) return
  let completedCopies = story?.share_requests?.filter((s) => s?.mode == 'copy' && s?.response == 'complete')
  let uniqueCopies = _.uniqBy(completedCopies, 'to_station')
  return uniqueCopies.length ?? 0
}
function getBasename(path) {
  if (!path) return null
  return decodeURIComponent(path.split('/').pop(-1))
}

/**
 * Parse a date value into a dayjs object in the workspace timezone.
 * Handles ISO strings, Unix timestamps (seconds), and Date objects.
 */
function parseDateToTz(dateValue) {
  if (!dateValue) return null
  const parsed = typeof dateValue === 'number' ? dayjs.unix(dateValue) : dayjs(dateValue)
  if (!parsed.isValid()) return null
  const tz = vuex.getters.currentTimezone
  return tz ? parsed.tz(tz) : parsed
}

/**
 * Format a date value as "MMM D, YYYY h:mm A" in the workspace timezone.
 */
function formatDateTime(dateValue) {
  const d = parseDateToTz(dateValue)
  return d ? d.format('MMM D, YYYY h:mm A') : ''
}

/**
 * Format a date value as relative time ("5m ago") when recent,
 * falling back to "MMM D, YYYY h:mm A" for older dates.
 */
function formatRelativeDate(dateValue) {
  const d = parseDateToTz(dateValue)
  if (!d) return ''
  const now = dayjs()
  const diffMins = now.diff(d, 'minute')
  const diffHours = now.diff(d, 'hour')
  const diffDays = now.diff(d, 'day')
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return d.format('MMM D, YYYY h:mm A')
}

function formatDate(params) {
  const parsed = dayjs(params?.value)
  if (parsed.isValid()) {
    if (params.column.actualWidth < 100) return parsed.format('MM/DD')
    return parsed.format('ddd, MMM D, YYYY')
  }
  return params?.value ?? 'TBD'
}
function dateValue(params) {
  if (params.data?.is_task) return params.data?.start_date ?? 'TBD'
  return params.data?.story_date
}
function assignmentComparator(value1, value2, nodeA, nodeB, isInverted) {
  const countA = value1?.length ?? 0
  const countB = value2?.length ?? 0
  return countB - countA
}

function stringComparator(value1, value2, nodeA, nodeB, isInverted) {
  if (typeof value1 === 'string' && typeof value2 === 'string') {
    if (isInverted) {
      return !value2 ? 1 : !value1 ? -1 : value1.localeCompare(value2)
    } else {
      return !value1 ? 1 : !value2 ? -1 : value1.localeCompare(value2)
    }
  }
  if (typeof value1 === 'number' && typeof value2 === 'number') {
    if (isInverted) {
      return value1 < value2 ? 1 : -1
    } else {
      return value2 > value1 ? 1 : -1
    }
  }
  if (typeof value1 === 'boolean' && typeof value2 === 'boolean') {
    if (isInverted) {
      return value1 < value2 ? 1 : -1
    } else {
      return value2 > value1 ? 1 : -1
    }
  }
  return 1
}
function numberComparator(value1 = 0, value2 = 0, nodeA, nodeB, isInverted) {
  return value2 - value1
}

function dateComparator(value1, value2, nodeA, nodeB, isInverted) {
  let d1 = Date.parse(value1)
  if (isNaN(d1) === true) d1 = Number.MAX_SAFE_INTEGER
  let d2 = Date.parse(value2)
  if (isNaN(d2) === true) d2 = Number.MAX_SAFE_INTEGER
  return d1 - d2
}

// Marks the comments as viewed for a specific story by storing a timestamp in localStorage.
// Used to track which stories have had their comments opened, enabling UI updates (unbolding).
function markCommentsViewed(storyId) {
  const viewedKey = `viewed_comment_${storyId}`
  localStorage.setItem(viewedKey, Date.now())
}

/**
 * Derive a story's StoryHub summary: the oldest comment's text, with HTML tags stripped.
 */
function getStorySummary(story) {
  const comments = story?.comments
  if (!comments?.length) return null
  const oldest = [...comments].sort((a, b) => new Date(a.comment_date) - new Date(b.comment_date))[0]
  return oldest?.comment?.replace(/<[^>]*>/g, '') || null
}

/**
 * Normalize comment/summary content for consistent HTML rendering.
 * Legacy plain-text comments (with \n newlines) are converted to <p> tags
 * so they render identically to TipTap-authored HTML comments.
 */
function normalizeCommentHtml(content) {
  if (!content) return content
  // If content already has block-level HTML structure, return as-is
  if (/<(p|div|h[1-6]|br|ul|ol|li)\b/i.test(content)) return content
  // Convert plain text newlines to paragraph tags
  return content
    .split('\n')
    .map((line) => `<p>${line}</p>`)
    .join('')
}

export const utils = {
  stagehandUtils,
  systemTemplates,
  getUniqShares,
  isValidFileName,
  hasInvalidFileNameChars,
  sanitizeFilename,
  INVALID_FILENAME_CHARS,
  isImage,
  downloadFile,
  isStoryArchived,
  isStoryEditable,
  isMediaAssetEditable,
  isStoryDownloadable,
  isMediaAssetDownloadable,
  getMediaExpiration,
  dateFormat,
  parseDate,
  getDateForView,
  minutesFormat,
  parseMinutes,
  isDark,
  today,
  countWords,
  getText,
  transformEnpsTags,
  parseEnpsTag,
  wrapText,
  detectWordSequence,
  parseSequentialNumbers,
  removeTagsWithClass,
  removeEmptyTags,
  toTitleCase,
  levenshtein,
  findSimilar,
  escape,
  groupStoriesBy,
  scriptListMergeInto,
  generatePlaceholderHtml,
  getEmployeeName,
  avgWPM: 180,
  getBasename,
  formatDate,
  formatDateTime,
  formatRelativeDate,
  dateValue,
  assignmentComparator,
  stringComparator,
  numberComparator,
  dateComparator,
  markCommentsViewed,
  normalizeCommentHtml,
  getStorySummary
}
